import { db } from '@/db';
import {
	InsertShiftEmployees,
	ShiftEmployees,
	schemaShiftEmployees,
} from '@/db/schema';
import { eq } from 'drizzle-orm';
import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

export const shiftEmployeesFn = createServerFn({ method: 'GET' }).handler(
	async () => {
		const results = await db
			.select()
			.from(schemaShiftEmployees)
			.orderBy(schemaShiftEmployees.id);
		return results as ShiftEmployees[];
	},
);

export const updateShiftEmployeesPayloadSchema = z.object({
	shifts: z
		.array(
			z.object({
				id: z.number().int().nonnegative(),
				name: z.string().optional(),
				employees: z.number().int().min(0).max(9999),
			}),
		)
		.min(1),
});

export type UpdateShiftEmployeesPayload = z.infer<
	typeof updateShiftEmployeesPayloadSchema
>;

type UpdatePayload = UpdateShiftEmployeesPayload;

type UpdateShift = UpdatePayload['shifts'][number];

const ensureShiftExists = async (shift: UpdateShift) => {
	const [existing] = await db
		.select()
		.from(schemaShiftEmployees)
		.where(eq(schemaShiftEmployees.id, shift.id))
		.limit(1);

	if (!existing) {
		throw new Error(`Shift with id ${shift.id} does not exist.`);
	}

	return existing as ShiftEmployees;
};

export const updateShiftEmployeesFn = createServerFn({ method: 'POST' })
	.inputValidator((data: UpdatePayload) =>
		updateShiftEmployeesPayloadSchema.parse(data),
	)
	.handler(async ({ data }) => {
		const updates: ShiftEmployees[] = [];

		for (const shift of data.shifts) {
			await ensureShiftExists(shift);

			await db
				.update(schemaShiftEmployees)
				.set({
					employees: shift.employees,
					...(shift.name
						? { name: shift.name as InsertShiftEmployees['name'] }
						: {}),
				})
				.where(eq(schemaShiftEmployees.id, shift.id));

			const [updated] = await db
				.select()
				.from(schemaShiftEmployees)
				.where(eq(schemaShiftEmployees.id, shift.id));

			if (updated) {
				updates.push(updated as ShiftEmployees);
			}
		}

		return updates;
	});
